// FoodSection.jsx
// ─ All emojis removed

import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

/* ─────────────────────────────────────────────────────────
   DATA — emoji field removed
───────────────────────────────────────────────────────── */
const FOOD_ITEMS = [
  {
    titleJa: 'ダルバート',
    titleEn: 'Dal Bhat',
    descJa:  '本格ネパール定食',
    descEn:  'Authentic Nepali set meal',
    image:   '/images/naan.png',
    bg:      'from-[#1a2a0a] to-[#2d4a14]',
  },
  {
    titleJa: '焼きたてナン',
    titleEn: 'Fresh Naan',
    descJa:  'タンドール窯で焼く香ばしいナン',
    descEn:  'Freshly baked naan from tandoor oven',
    image:   '/images/naan.png',
    bg:      'from-[#3a2010] to-[#6b3a1f]',
  },
  {
    titleJa: 'チキンカレー',
    titleEn: 'Chicken Curry',
    descJa:  'スパイス香る本格カレー',
    descEn:  'Authentic spiced curry',
    image:   '/images/naan.png',
    bg:      'from-[#3a1a0a] to-[#6b3014]',
  },
  {
    titleJa: '手ぶらBBQ',
    titleEn: 'BBQ',
    descJa:  '道具・食材すべて込み！',
    descEn:  'Everything included for BBQ',
    image:   '/images/naan.png',
    bg:      'from-[#2a1a0a] to-[#5a3014]',
  },
];

const MINI_CARDS = [
  {
    src:     '/images/naan.png',
    bg:      'from-[#2d1a0a] to-[#5a3a1a]',
    titleJa: '手ぶらBBQ',
    titleEn: 'BBQ',
    descJa:  '食材・道具すべて込み',
    descEn:  'Everything included',
    color:   'text-[#7b4318]',
  },
  {
    src:     '/images/naan.png',
    bg:      'from-[#3a1a0a] to-[#6b3014]',
    titleJa: '本格カレー',
    titleEn: 'Nepali Curry',
    descJa:  'スパイス香る本場の味',
    descEn:  'Rich authentic spices',
    color:   'text-[#234223]',
  },
];

/* ─────────────────────────────────────────────────────────
   SECTION LABEL
───────────────────────────────────────────────────────── */
function SectionLabel({ text }) {
  return (
    <div className="flex items-center justify-center gap-4 mb-5">
      <div className="flex-1 max-w-[80px] h-[3px] rounded-full bg-gradient-to-r from-transparent to-[#9a6326]" />
      <span className="text-[11px] font-extrabold tracking-[0.35em] uppercase text-[#9a6326]">{text}</span>
      <div className="flex-1 max-w-[80px] h-[3px] rounded-full bg-gradient-to-l from-transparent to-[#9a6326]" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────── */
export default function FoodSection() {
  const { i18n } = useTranslation();
  const isJa = i18n.language?.startsWith('ja');

  return (
    <section id="food" className="relative overflow-hidden py-16 px-4 bg-[#f7f2e8]">

      {/* dot pattern */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage:'radial-gradient(circle,rgba(100,60,20,.04) 1px,transparent 1px)', backgroundSize:'24px 24px' }}/>

      <div className="max-w-7xl mx-auto relative z-10">

        {/* ── SECTION LABEL ── */}
        <SectionLabel text="Restaurant & BBQ · お食事" />

        {/* ══ HEADER ══ */}
        <motion.div
          initial={{ opacity:0, y:40 }} whileInView={{ opacity:1, y:0 }}
          transition={{ duration:.7 }} viewport={{ once:true }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-3 bg-[#efe4cf] border-[2px] border-[#d7c3a1] rounded-full px-7 py-3 mb-5 shadow-[0_4px_0_#d7c3a1]">
            <span className="text-[16px] font-extrabold text-[#234223]">Restaurant & BBQ</span>
          </div>

          <h2 className="font-extrabold text-[#173617] leading-[.92] tracking-tight mb-4"
            style={{ fontSize:'clamp(44px,7vw,72px)' }}>
            {isJa ? (
              <>グルメ＆<span className="text-[#9a6326]">レストラン</span></>
            ) : (
              <>Restaurant <span className="text-[#9a6326]">& Food</span></>
            )}
          </h2>

          <p className="text-[16px] font-bold text-[#666] max-w-lg mx-auto leading-[1.8]">
            {isJa
              ? 'アウトドア体験の後は、美味しい料理でひと休み'
              : 'Relax and enjoy authentic food after adventure'}
            <span className="block text-[13px] text-[#999] mt-1">
              {isJa
                ? 'Relax and enjoy authentic food after adventure'
                : 'アウトドア体験の後は美味しい料理でひと休み'}
            </span>
          </p>
        </motion.div>

        {/* ══ HERO GRID ══ */}
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-4 mb-4">

          {/* MAIN CARD */}
          <motion.div
            initial={{ opacity:0, x:-40 }} whileInView={{ opacity:1, x:0 }}
            transition={{ duration:.7 }} viewport={{ once:true }}
            className="
              bg-white rounded-[28px] overflow-hidden
              border-[2.5px] border-[#ddcfb7]
              shadow-[0_12px_0_#ddcfb7,0_18px_40px_rgba(30,40,20,.08)]
              relative
            "
          >
            {/* rainbow top border */}
            <div className="absolute top-0 left-0 right-0 h-[6px] z-10"
              style={{ background:'repeating-linear-gradient(90deg,#9a6326 0,#9a6326 40px,#234223 40px,#234223 80px,#c8340a 80px,#c8340a 120px)' }}/>

            <div className="grid md:grid-cols-2">

              {/* image side */}
              <div className="relative min-h-[420px] bg-gradient-to-br from-[#1a2a0a] to-[#2d4a14]">
                <img
                  src="/images/paprika.jpg"
                  alt="Paprika Restaurant"
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"/>
                {/* PAPRIKA badge */}
                <div className="absolute bottom-6 left-6 z-10 bg-white/12 backdrop-blur-xl border border-white/25 rounded-2xl px-5 py-4">
                  <p className="text-[10px] font-extrabold tracking-[.22em] uppercase text-white/60 mb-1">Nepali Restaurant</p>
                  <p className="text-[32px] font-extrabold text-white leading-none">PAPRIKA</p>
                </div>
              </div>

              {/* content side */}
              <div className="p-9 flex flex-col justify-center">
                <p className="text-[12px] font-extrabold tracking-[.18em] uppercase text-[#9a6326] mb-1">
                  {isJa ? 'ネパールレストラン＆バー' : 'Nepali Restaurant & Bar'}
                </p>
                <h3 className="font-extrabold text-[#173617] leading-tight mb-4"
                  style={{ fontSize:'clamp(28px,3.5vw,40px)' }}>
                  {isJa ? '本格ネパール料理' : 'Authentic Nepali Food'}
                </h3>
                <p className="text-[14px] font-semibold text-[#555] leading-[1.85] mb-5">
                  {isJa
                    ? 'スパイス香る本格カレー、焼きたてナン、ダルバートなど、本場ネパールの味を自然の中で楽しめます。'
                    : 'Enjoy authentic curry, fresh naan, dal bhat and more in a relaxing outdoor atmosphere.'}
                </p>

                {/* BBQ highlight */}
                <div className="bg-[#234223] rounded-[18px] px-5 py-5 shadow-[0_4px_0_#142814]">
                  <p className="text-[20px] font-extrabold text-white mb-1">
                    {isJa ? '手ぶらBBQ' : 'BBQ'}
                  </p>
                  <p className="text-[13px] font-bold text-white/80 leading-[1.7]">
                    {isJa
                      ? '食材・機材すべてご用意します。手ぶらでOK！'
                      : 'Everything included. Just come and enjoy.'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT SIDE */}
          <div className="flex flex-col gap-3">

            {/* naan card */}
            <motion.div
              initial={{ opacity:0, x:40 }} whileInView={{ opacity:1, x:0 }}
              transition={{ duration:.7 }} viewport={{ once:true }}
              className="
                relative rounded-[22px] overflow-hidden h-[240px]
                border-[2.5px] border-[#ddcfb7]
                shadow-[0_6px_0_#ddcfb7]
                bg-gradient-to-br from-[#3a2010] to-[#6b3a1f]
              "
            >
              <img
                src="/images/naan.png"
                alt="Fresh Naan"
                className="absolute inset-0 w-full h-full object-cover"
                onError={e => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-black/30"/>
              <div className="absolute bottom-4 left-4 z-10 bg-black/35 backdrop-blur-md rounded-[14px] px-4 py-3">
                <p className="text-[20px] font-extrabold text-white leading-none">Fresh Naan</p>
                <p className="text-[11px] font-bold text-white/70 mt-1">焼きたてナン · Tandoor Oven</p>
              </div>
            </motion.div>

            {/* 2 mini cards */}
            <div className="grid grid-cols-2 gap-3">
              {MINI_CARDS.map((item, i) => (
                <motion.div
                  key={i}
                  whileHover={{ y:-6 }}
                  transition={{ type:'spring', stiffness:300, damping:22 }}
                  className="
                    bg-white rounded-[20px] overflow-hidden
                    border-[2.5px] border-[#ddcfb7]
                    shadow-[0_5px_0_#ddcfb7,0_8px_18px_rgba(30,40,20,.06)]
                  "
                >
                  <div className={`relative h-[130px] bg-gradient-to-br ${item.bg}`}>
                    <img
                      src={item.src}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="p-3.5">
                    <h4 className={`text-[16px] font-extrabold mb-1 ${item.color}`}>
                      {isJa ? item.titleJa : item.titleEn}
                    </h4>
                    <p className="text-[11px] font-bold text-[#777] leading-[1.55]">
                      {isJa ? item.descJa : item.descEn}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ FOOD ITEM GRID ══ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {FOOD_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity:0, y:40 }}
              whileInView={{ opacity:1, y:0 }}
              transition={{ duration:.5, delay: i * 0.09 }}
              viewport={{ once:true }}
              whileHover={{ y:-8 }}
              className="
                bg-white rounded-[22px] overflow-hidden
                border-[2.5px] border-[#ddcfb7]
                shadow-[0_6px_0_#ddcfb7,0_10px_24px_rgba(30,40,20,.07)]
              "
            >
              {/* image — gradient fallback only, no emoji */}
              <div className={`relative h-[180px] bg-gradient-to-br ${item.bg}`}>
                <img
                  src={item.image}
                  alt={isJa ? item.titleJa : item.titleEn}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>

              {/* body */}
              <div className="p-4">
                <h3 className="text-[18px] font-extrabold text-[#173617] mb-1.5 leading-tight">
                  {isJa ? item.titleJa : item.titleEn}
                </h3>
                <p className="text-[12px] font-bold text-[#666] leading-[1.65]">
                  {isJa ? item.descJa : item.descEn}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}